import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UNIT_TYPE_OPTIONS = [
  { value: 'Treater Truck', label: 'Treater Truck' },
  { value: 'Chemical Delivery Truck', label: 'Chemical Delivery Truck' },
  { value: 'Tractor', label: 'Tractor' },
  { value: 'Trailer', label: 'Trailer' },
  { value: 'Pickup', label: 'Pickup' },
  { value: 'Other', label: 'Other' }
];

const STATUS_OPTIONS = [
  { value: 'Available', label: 'Available' },
  { value: 'In Use', label: 'In Use' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'Out of Service', label: 'Out of Service' }
];

const AddEditUnitModal = ({ isOpen, onClose, unit, onSave, yards = [] }) => {
  const [formData, setFormData] = useState({
    unitNumber: '',
    vin: '',
    make: '',
    model: '',
    year: '',
    engine: '',
    esn: '',
    unitType: '',
    status: 'Available',
    yardId: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const yardOptions = yards?.map(y => ({ value: y?.id, label: y?.name })) || [];

  useEffect(() => {
    if (unit) {
      setFormData({
        unitNumber: unit?.unitNumber || '',
        vin: unit?.vin || '',
        make: unit?.make || '',
        model: unit?.model || '',
        year: unit?.year?.toString() || '',
        engine: unit?.engine || '',
        esn: unit?.esn || '',
        unitType: unit?.unitType || '',
        status: unit?.status || 'Available',
        yardId: unit?.yardId || ''
      });
    } else {
      setFormData({
        unitNumber: '',
        vin: '',
        make: '',
        model: '',
        year: '',
        engine: '',
        esn: '',
        unitType: '',
        status: 'Available',
        yardId: ''
      });
    }
    setErrors({});
    setSaveError(null);
  }, [unit, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.unitNumber?.trim()) {
      newErrors.unitNumber = 'Unit number is required';
    }

    if (!formData?.vin?.trim()) {
      newErrors.vin = 'VIN is required';
    } else if (formData?.vin?.length !== 17) {
      newErrors.vin = 'VIN must be exactly 17 characters';
    }

    if (formData?.year) {
      const yearNum = parseInt(formData?.year);
      const currentYear = new Date()?.getFullYear();
      if (yearNum < 1900 || yearNum > currentYear + 1) {
        newErrors.year = `Year must be between 1900 and ${currentYear + 1}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSaveError(null);

    try {
      await onSave({
        ...formData,
        year: formData?.year ? parseInt(formData?.year) : null
      });
      // onSave resolves only on success; parent closes modal
    } catch (error) {
      setSaveError(error?.message || 'Failed to save unit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[210] p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-in">
          <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="Truck" size={24} strokeWidth={2} className="text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  {unit ? 'Edit Unit' : 'Add New Unit'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-hover"
              >
                <Icon name="X" size={24} strokeWidth={2} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Unit Number"
                type="text"
                placeholder="Enter unit number"
                value={formData?.unitNumber}
                onChange={(e) => handleChange('unitNumber', e?.target?.value)}
                error={errors?.unitNumber}
                required
              />

              <Input
                label="VIN"
                type="text"
                placeholder="17-character VIN"
                value={formData?.vin}
                onChange={(e) => handleChange('vin', e?.target?.value?.toUpperCase())}
                error={errors?.vin}
                maxLength={17}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Make"
                type="text"
                placeholder="e.g., Freightliner"
                value={formData?.make}
                onChange={(e) => handleChange('make', e?.target?.value)}
                error={errors?.make}
              />

              <Input
                label="Model"
                type="text"
                placeholder="e.g., Cascadia"
                value={formData?.model}
                onChange={(e) => handleChange('model', e?.target?.value)}
                error={errors?.model}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Year"
                type="number"
                placeholder="e.g., 2022"
                value={formData?.year}
                onChange={(e) => handleChange('year', e?.target?.value)}
                error={errors?.year}
                min="1900"
                max={new Date()?.getFullYear() + 1}
              />

              <Input
                label="Engine"
                type="text"
                placeholder="e.g., Cummins X15"
                value={formData?.engine}
                onChange={(e) => handleChange('engine', e?.target?.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="ESN (Engine Serial Number)"
                type="text"
                placeholder="Enter engine serial number"
                value={formData?.esn}
                onChange={(e) => handleChange('esn', e?.target?.value)}
              />

              <Select
                label="Unit Type"
                options={UNIT_TYPE_OPTIONS}
                value={formData?.unitType}
                onChange={(value) => handleChange('unitType', value)}
                placeholder="Select unit type"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={formData?.status}
                onChange={(value) => handleChange('status', value)}
                placeholder="Select status"
              />

              <Select
                label="Yard Location"
                options={yardOptions}
                value={formData?.yardId}
                onChange={(value) => handleChange('yardId', value)}
                placeholder="Select yard (optional)"
              />
            </div>

            {saveError && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 text-destructive rounded-lg">
                <Icon name="AlertCircle" size={18} strokeWidth={2} />
                <span className="text-sm">{saveError}</span>
              </div>
            )}

            <div className="flex items-center space-x-3 pt-4">
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isSubmitting}
                iconName="Save"
                iconPosition="left"
              >
                {unit ? 'Update Unit' : 'Add Unit'}
              </Button>
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddEditUnitModal;